import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, MessageSquare, RefreshCw, Package, Target, Brain, TrendingUp, Zap, Shield, Globe2, Clock, Cpu, Bot, ArrowRight, Palette, BarChart3, Settings, Database, Headphones, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SHOPIFY_INSTALL_URL } from '@/components/Navbar';

const FEATURES_HERO_IMG = '/assets/features-hero.jpg';
const FEATURES_SIDE_IMG = '/assets/features-page-side.jpg';

const FeaturesPage = () => {
  const features = [
    {
      icon: Brain,
      title: 'Smart Product Recommendations',
      description: 'AI recommends products based on typed keywords and browsing patterns, guiding users to the right pages instantly.',
      color: 'bg-teal-50 border-teal-200',
      iconBg: 'bg-teal-600'
    },
    {
      icon: MessageSquare,
      title: 'AI-Generated Replies',
      description: 'Advanced NLP generates human-like responses that understand context and drive engagement and sales.',
      color: 'bg-cyan-50 border-cyan-200',
      iconBg: 'bg-cyan-600'
    },
    {
      icon: Palette,
      title: 'Customize Appearance',
      description: 'Match your chatbot\'s design with your brand identity by customizing colors, avatar, and messaging style.',
      color: 'bg-pink-50 border-pink-200',
      iconBg: 'bg-pink-600'
    },
    {
      icon: Languages,
      title: 'Multilingual Support',
      description: 'Sell globally with AI that chats fluently in multiple languages, breaking the language barrier for international buyers.',
      color: 'bg-purple-50 border-purple-200',
      iconBg: 'bg-purple-600'
    },
    {
      icon: BarChart3,
      title: 'Analytics Integration',
      description: 'AI-powered insights into customer conversations, engagement metrics, and sales attribution in your dashboard.',
      color: 'bg-sky-50 border-sky-200',
      iconBg: 'bg-sky-600'
    },
    {
      icon: Settings,
      title: 'Bot Behaviour Control',
      description: 'Control your chatbot\'s personality, customize responses, set its role, and define how it interacts with customers.',
      color: 'bg-orange-50 border-orange-200',
      iconBg: 'bg-orange-600'
    },
    {
      icon: Database,
      title: 'Data Collection Pipelines',
      description: 'Automatically collect and organize customer data, preferences, and interaction history for better targeting.',
      color: 'bg-rose-50 border-rose-200',
      iconBg: 'bg-rose-600'
    },
    {
      icon: Bot,
      title: 'Self-Learning AI',
      description: 'Machine learning models continuously improve based on customer interactions and conversion data.',
      color: 'bg-violet-50 border-violet-200',
      iconBg: 'bg-violet-600'
    },
    {
      icon: Headphones,
      title: 'Multichannel Communication',
      description: 'Engage customers across WhatsApp, Instagram, Facebook, Telegram, Email, Phone, and Live Chat from one dashboard.',
      color: 'bg-green-50 border-green-200',
      iconBg: 'bg-green-600'
    }
  ];

  const additionalFeatures = [
    { icon: Zap, title: 'Instant Deploy', description: 'Setup in under 5 minutes' },
    { icon: Shield, title: 'Enterprise Security', description: 'Bank-grade encryption' },
    { icon: Globe2, title: 'Multi-language', description: 'Languages supported' },
    { icon: Clock, title: '24/7 Active', description: 'AI never sleeps' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="features-page">
      <Navbar />
      
      <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        {/* Hero with Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
                <Cpu className="w-4 h-4 text-teal-600" />
                <span className="text-xs sm:text-sm font-bold text-teal-700">AI Capabilities</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 sm:mb-6 leading-tight">
                Powerful AI Features for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">E-commerce</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
                Arismate combines cutting-edge AI with deep e-commerce integration to transform how you sell online.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img 
                src={FEATURES_HERO_IMG}
                alt="Customer enjoying AI-powered shopping experience"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[260px] sm:h-[316px] object-cover border-2 border-slate-100"
                data-testid="features-hero-image"
              />
              <motion.div 
                className="absolute -bottom-4 -left-4 bg-teal-600 text-white px-4 py-2 rounded-xl shadow-lg text-sm font-bold flex items-center gap-2"
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 2.5 }}
              >
                <Brain className="w-4 h-4" />
                6+ AI Features
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Main Features */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 ${feature.color} shadow-sm hover:shadow-xl transition-all duration-300 group`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-14 sm:w-16 h-14 sm:h-16 rounded-xl sm:rounded-2xl ${feature.iconBg} flex items-center justify-center mb-4 sm:mb-6 shadow-lg`}>
                  <feature.icon className="w-7 sm:w-8 h-7 sm:h-8 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
                <div className="flex items-center gap-2 text-teal-600 mt-4 text-sm font-medium">
                  <TrendingUp className="w-4 h-4" />
                  <span>Boosts conversions</span>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Showcase Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <motion.div 
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={FEATURES_SIDE_IMG}
              alt="Team collaborating on AI e-commerce solutions"
              className="w-full h-[200px] sm:h-[300px] object-cover"
              data-testid="features-showcase-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-transparent flex items-center">
              <div className="p-6 sm:p-10 max-w-lg">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white mb-2 sm:mb-3">Built for Results</h3>
                <p className="text-sm sm:text-base text-teal-100">Every feature is designed to increase your store's revenue, not just answer questions.</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Additional Features Grid */}
        <section className="bg-slate-50 border-y border-slate-200 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <motion.div 
              className="text-center mb-10 sm:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 sm:mb-5">And Much More</h2>
              <p className="text-base sm:text-lg text-slate-600">Built with everything your store needs to succeed</p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {additionalFeatures.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 text-center border-2 border-slate-100 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl sm:rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mx-auto mb-3 sm:mb-5">
                    <item.icon className="w-6 sm:w-7 h-6 sm:h-7 text-teal-600" />
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm sm:text-lg mb-1 sm:mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-xs sm:text-base">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 sm:mb-6">
                Ready to Supercharge Your Store?
              </h2>
              <p className="text-base sm:text-lg text-slate-600 mb-8 px-4">
                Deploy Arismate AI and start converting more visitors into customers today.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white group font-bold px-8" data-testid="features-install-btn">
                    <Cpu className="w-5 h-5 mr-2" />
                    Install on Shopify
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 hover:bg-teal-50 font-bold px-8" data-testid="features-contact-btn">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
